import * as mentioningRegex from './mentioningRegex';

/**
 * Given an array of user objects rom the API,
 * formats their properties in a way
 * that can be read by `react-mentioning`
 * to render the list of people to mention.
 * @param {array} users
 */
export const formatMentioningResults = (users) =>
  users.map((user) => ({
    display: user.name,
    id: user.id,
    avatar: user.avatar_url,
    isCommunityMember: user.member_of_current_community
  }));

/**
 * In order for the API to correctly parse a mention,
 * it needs to be templated as `{@|__id__|user}`.
 * This template does not include the actual name of the user,
 * which `react-mentioning` needs to show a name in the input box.
 * Therefore the `display` property
 * is tacked on at the end of the template and removed
 * before it is sent to the API .
 */
export const MENTION_INPUT_MARKUP = '{@|__id__|user}[<__display__>]';

/**
 * Given an id and a display name,
 * renders how the mention should appear in the coment input box.
 * id is not used, but these parameters must
 * match the function signature that `react-mentioning` uses.
 * @param {number} id
 * @param {string} display
 */
export const renderMentionedUserInInput = (id, display) => `@${display}`;

/**
 * Takes a comment with mentioning templates
 * and shortens them to only return the part the API needs.
 * e.g. {@|12345|user}[<Giancarlo Galliani>] => {@|12345|user}
 * @param {string} comment Comment text with templates to be sortened
 */
export const shortenMentioningTemplates = (comment) =>
  comment.replace(mentioningRegex.mentionWithExtraTemplate(), '$1');

/**
 * Determines if a given mention
 * is in the comment's list of
 * mentioned users
 * @param {string} mention
 * @param {array} userMentionList Array of mentioned user objects from the API
 */
export const getMentionedUser = (mention, userMentionList = []) => {
  return userMentionList.find(({ name }) => mention.includes(name)) || {};
};

/**
 * Determines if a string is a
 * mention based on the presence of
 * the invisible separator characters (\u2063) or (\200B)
 * or follow the {@|3|user}[<John Citizen>] template
 * @param {string} text
 */
export const isMention = (text) => {
  return (
    mentioningRegex.invisibleSeparator().test(text) ||
    mentioningRegex.mentionWithValidTemplate().test(text)
  );
};

/**
 * Given a string, converts any plain-text "*U+2063*" or "*U+200B*"
 * sequences into the invisible character (\u2063) or (\200B) respectively,
 * they are meant to represent.
 * This is required because the API
 * returns the "invisible" character code as plain text.
 * @param {string} text
 */
const transformInvisibleCharacters = (text) =>
  text.replace(
    /\*U\+2063\*|\*U\+200B\*/g, // Supports legacy (2063) and current (200B) mention boundaries
    mentioningRegex.INVISIBLE_SEPARATOR_CHARACTER
  );

/**
 * This function takes text and splits it
 * into an array of objects to separate plain text and mentions
 * Each array contains an id, the text itself
 * and whether it is a valid mention
 * Valid mentions are determined by
 * whether they are currently in the mentioning list
 *
 * @param {String} text
 * @param {Array} userMentionList
 */
export const generateSplitFunction = (regex) => (text = '') => {
  return text.split(regex).filter((el) => el && el.length !== 0);
};

/**
 * Function that splits comments up
 * into mention and non-mention parts.
 * @example "Hello @Bob how are you?" => ["Hello ", "@Bob", " how are you?"]
 */
export const splitByValidMentions = (regex) => generateSplitFunction(regex);

/**
 * Extracts the mentioned user's name from the string of text aka chunk
 * @param {String} chunk - string of text to extract the mention name from
 * @returns {String} - name of the mentioned user
 */
const getMentionNameFromChunk = (chunk) =>
  chunk.replace(mentioningRegex.getMentionName(), '$1');

/**
 * Takes an array of comment text chunks,
 * and outputs a mapping function that takes
 * an array of mentioned users as a argument.
 *
 * Calling this function with the mentioned users
 * will generate an array of objects,
 * one for each chunk of the comment.
 *
 * The chunks that are actually mentions will
 * have different properties, so they can be formatted
 * differently in the comment.
 * @param {array} splitComment Comment that has been split into an array
 */
export const generateCommentMapper = (splitComment, userMentionList) =>
  splitComment.map((textChunk, i) => {
    const isChunkAMention = isMention(textChunk);
    const mentionedUser = isChunkAMention
      ? getMentionedUser(textChunk, userMentionList)
      : {};
    return {
      chunkId: i,
      text: isChunkAMention ? getMentionNameFromChunk(textChunk) : textChunk,
      isMention: isChunkAMention && Boolean(mentionedUser.id),
      mentionedUserId: mentionedUser.id,
      mentionedName: mentionedUser.name
    };
  });

/**
 * Given text string and a list of users,
 * will output an array of comment chunk objects.
 * @param {string} comment The comment to be chunked
 * @param {array} userMentionList Array of users that are mentioned in the comment
 */
export const createCommentChunkArray = (
  comment,
  userMentionList,
  isFromApi = false
) => {
  let validMentions = [];
  const transformedComment = transformInvisibleCharacters(comment);
  const regexTemplate = isFromApi
    ? mentioningRegex.mentionWithNoPrecedingSpace()
    : mentioningRegex.mentionWithValidTemplate();
  // Text from the api needs an additional sanitization step
  validMentions = splitByValidMentions(regexTemplate)(transformedComment);

  return generateCommentMapper(validMentions, userMentionList);
};

/**
 * Takes a string of text and a list of mentioned users and formats
 * the mentioned users (if any) into a template react-mentions will understand.
 * @param {String} commentText - string to be formatted
 * @param {Array<Object>} mentionedUsers - list of mentioned users
 * @returns {String} formated string with mentions or the original string
 */
export const formatCommentFromApi = (commentText, mentionedUsers) => {
  // No need to format anything if we have no mentions to format
  if (mentionedUsers) {
    // Reduces and transforms the array of string down to a single string.
    const reducer = (accumulator, currentChunk) => {
      const {
        isMention: isMentionChunk,
        mentionedUserId,
        mentionedName,
        text: chunkText
      } = currentChunk;
      if (isMentionChunk) {
        const formattedChunk = `{@|${mentionedUserId}|user}[<${mentionedName}>]`;
        return accumulator.concat(formattedChunk);
      }
      return accumulator.concat(chunkText);
    };

    // Returns the final formated string
    return createCommentChunkArray(commentText, mentionedUsers, true).reduce(
      reducer,
      ''
    );
  }
  return commentText;
};

/** Based on original comment that contains mentioned users updates translated comments with mentions.
 *  After this pass updated translated comment to a data processor that will handle result.
 * @param dataProcessor - fn that will handle/save result
 * @returns result - data processor result on updated translated comment
 */
export const updateMentionsBasedOnComments = (dataProcessor) => (
  comment,
  translatedComment
) => {
  let result = translatedComment;

  if (comment.mentioned_users) {
    comment.mentioned_users.forEach((user) => {
      const regex = new RegExp(`@${user.name}`, 'g');
      result = result.replace(regex, `{@|${user.id}|user}[<${user.name}>]`);
    });
  }
  return dataProcessor(comment, result);
};
